import { bundledLocaleXML } from './csl/bundled-locales';
import { getDefaults } from './csl';

/**
 * The words a reference line uses to say it has no date, as CSL itself defines them: the `no date` term
 * (long and short form) of every locale this package ships, plus the default en-US locale. This is data from
 * the CSL locale files, not a word list kept here, so a locale added to `bundled-locales.ts` is covered by
 * adding that locale file alone. A locale the package does not ship is not covered.
 *
 * A term matches in the case the locale file writes it, never case-insensitively: `o. J.` is German for "no
 * date", `O. J.` is an author's initials.
 */
function collectNoDateTerms(): string[] {
  const xmls = [getDefaults().localeXML, ...Object.values(bundledLocaleXML)];
  const terms = new Set<string>();
  for (const xml of xmls) {
    for (const m of xml.matchAll(/<term\s+name="no date"[^>]*>([^<]+)<\/term>/gu)) {
      const term = m[1]
        .replace(/&#160;|&nbsp;/gu, ' ')
        .replace(/\s+/gu, ' ')
        .trim();
      if (term) terms.add(term);
    }
  }
  return [...terms];
}

let cached: { terms: string[]; matcher: RegExp } | undefined;

function noDate(): { terms: string[]; matcher: RegExp } {
  if (cached) return cached;
  const terms = collectNoDateTerms();
  const source = terms
    .map((t) => t.split(' ').map((w) => w.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')).join('\\s*'))
    .join('|');
  // A term stands alone: not glued to a letter or digit on either side (`Bond. 2010` holds no `n.d.`).
  cached = { terms, matcher: new RegExp(`(?<![\\p{L}\\p{N}])(?:${source})(?![\\p{L}\\p{N}])`, 'u') };
  return cached;
}

/** The `no date` terms of the shipped CSL locales, as written in the locale files (case kept). */
export function noDateTerms(): string[] {
  return [...noDate().terms];
}

/** Whether a reference line states, in the words of a shipped CSL locale, that the work has no date. */
export function statesNoDate(line: string): boolean {
  return noDate().matcher.test(line.replace(/ /gu, ' '));
}
